import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout, Header } from '../components/mte/shell';
import { PasswordField } from '../components/form/PasswordField';
import { apiFetch, ApiError } from '../lib/auth-fetch';
import styles from './Entrer.module.css';

/**
 * Entrer — écran 2/11 (spec Boussole v2.1 ratif Ana 18/09 ~15:10).
 *
 * Page d'entrée publique — 2 cartes principales (Famille + Enseignant) +
 * bandeau unique "Après votre connexion". Décor : background monde magique +
 * Sábio + coruja CANON à gauche (imutável).
 *
 * Source spec : `boussole-docs/mte-entree-restructure-v2.md` @ wip/boussole-docs/bcd2afd.
 * Source mockup : `shared/mte-mockups/mte-entree-2026-09-18/ChatGPT-Image-2026-09-18_14_54_47-2cards-final.png`.
 *
 * Canon :
 * - 6 héros fixes (D-NX-038) — non affichés ici, on est en pré-sélection profil
 * - Professeur-sage (D-NX-042) — non affichés ici, on est en pré-sélection profil
 * - Mission Rentrée 15×6=90 (D-NX-021b) — référencé en doc, hors écran 2
 * - QTypeSchema 11 types (D-NX-021b) — hors écran 2
 * - D-IA-08 EntryChoice inline documenté en 04 §2.0 — cette page est
 *   la matérialisation dédiée après le CTA Homepage (séparation parent/enfant
 *   explicite, plus de EntryChoice inline)
 * - Oral ≠ IA (D-NX-007) — pas d'IA sur cette page
 * - Sans erreur technique enfant (D-OB-03)
 * - Règle enfant (Ana-direct 18/09 ~14:55) : aucune carte Enfant dans cette page.
 * - Phase 2 Enseignant démarre maintenant (Ana-direct 18/09 ~15:10) — CTA câblés.
 *
 * État actuel : Vague 1 (MVP visuel), écran 2 navigable.
 * Aucun contenu pédagogique réel ; pas d'animations finales.
 * Auth : réel (M-C2.0+ Phase 3+ wiring) — POST /api/auth/signin partagé.
 */

interface SignInErrors {
  email?: string;
  password?: string;
  submit?: string;
}

async function submitSignIn(
  role: 'famille' | 'enseignant',
  email: string,
  password: string,
  navigate: (path: string, options?: { replace?: boolean }) => void,
): Promise<void> {
  try {
    await apiFetch<{ userId: string; needsDeviceAuth: boolean }>(
      '/api/auth/signin',
      {
        method: 'POST',
        body: JSON.stringify({ email, password, role }),
      },
    );
    // Phase 2 Enseignant : serveur redirigera vers /espace-enseignant/ via role metadata.
    // MVP : tous rôles atterrissent sur /famille/accueil/ (= ChoixEnfant).
    navigate('/famille/accueil/', { replace: true });
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.code === 'unauth') throw new Error('Identifiants invalides.');
      throw new Error(e.message || 'Connexion impossible.');
    }
    throw new Error('Connexion impossible.');
  }
}

function Entrer(): JSX.Element {
  const navigate = useNavigate();

  // Form state — Famille (card 1)
  const [emailF, setEmailF] = useState('');
  const [passwordF, setPasswordF] = useState('');
  const [errorsF, setErrorsF] = useState<SignInErrors>({});
  const [loadingF, setLoadingF] = useState(false);

  // Form state — Enseignant (card 2)
  const [emailE, setEmailE] = useState('');
  const [passwordE, setPasswordE] = useState('');
  const [errorsE, setErrorsE] = useState<SignInErrors>({});
  const [loadingE, setLoadingE] = useState(false);

  const validate = (email: string, password: string): SignInErrors => {
    const e: SignInErrors = {};
    if (!email.trim()) e.email = 'Email requis.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Email invalide.';
    if (!password) e.password = 'Mot de passe requis.';
    return e;
  };

  const handleFamille = async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault();
    const v = validate(emailF, passwordF);
    setErrorsF(v);
    if (v.email || v.password) return;
    setLoadingF(true);
    try {
      await submitSignIn('famille', emailF, passwordF, navigate);
    } catch (e) {
      setErrorsF({ submit: e instanceof Error ? e.message : 'Connexion impossible.' });
    } finally {
      setLoadingF(false);
    }
  };

  const handleEnseignant = async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault();
    const v = validate(emailE, passwordE);
    setErrorsE(v);
    if (v.email || v.password) return;
    setLoadingE(true);
    try {
      await submitSignIn('enseignant', emailE, passwordE, navigate);
    } catch (e) {
      setErrorsE({ submit: e instanceof Error ? e.message : 'Connexion impossible.' });
    } finally {
      setLoadingE(false);
    }
  };

  return (
    <Layout
      context="adulte"
      width="wide"
      header={
        <Header
          context="adulte"
          logo={
            <>
              <span className={styles.brandMark} aria-hidden="true">✦</span>
              <span className={styles.brandText}>Mon Temps d'Étude</span>
            </>
          }
        />
      }
      footer={
        <footer className={styles.footer}>
          <p>
            <Link to="/">← Retour à l'accueil</Link>
            {' · '}
            <a href="/entree/qui/">Version sans JavaScript</a>
          </p>
          <p className={styles.footerMeta}>
            Vague 1 · D-NX-068 · écran 2/11 — v2 2026-09-18
          </p>
        </footer>
      }
    >
      <div className={styles.pageBackground} aria-hidden="true">
        <img
          src="/mte-entree-2026-09-18/background-magique.png"
          alt=""
          className={styles.bgImage}
        />
      </div>

      <img
        src="/mte-entree-2026-09-18/sabie-professeur-coruja-canon.png"
        alt=""
        aria-hidden="true"
        className={styles.sabioGuide}
      />

      <main className={styles.main} aria-labelledby="entrer-title">
        <div className={styles.intro}>
          <h1 id="entrer-title" className={styles.title}>
            Connecte-toi à ton univers
          </h1>
          <p className={styles.subtitle}>
            Retrouve tes enfants, leurs progrès et toutes leurs aventures au
            sein de Mon Temps d'Étude.
          </p>
        </div>

        <div className={styles.cards} role="group" aria-label="Choix du profil">
          {/* ---------- Card 1 · Famille ---------- */}
          <section
            className={`${styles.card} ${styles.cardFamille}`}
            aria-labelledby="card-famille-title"
          >
            <img
              src="/mte-entree-2026-09-18/moldure-carte-famille.png"
              alt=""
              aria-hidden="true"
              className={styles.cardFrame}
            />
            <div className={styles.cardInner}>
              <img
                src="/mte-entree-2026-09-18/icone-famille-or.png"
                alt=""
                aria-hidden="true"
                className={styles.cardIcon}
              />
              <h2 id="card-famille-title" className={styles.cardSubtitle}>
                Accédez à votre espace famille
              </h2>

              <form
                className={styles.cardForm}
                onSubmit={handleFamille}
                noValidate
                data-testid="famille-form"
              >
                <div className={styles.field}>
                  <label htmlFor="famille-email" className={styles.label}>
                    Votre adresse e-mail
                  </label>
                  <div
                    className={`${styles.emailWrap} ${errorsF.email ? styles.emailWrapError : ''}`}
                  >
                    <img
                      src="/mte-entree-2026-09-18/icone-email-bleu-nuit.png"
                      alt=""
                      aria-hidden="true"
                      className={styles.iconEmail}
                    />
                    <input
                      id="famille-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      className={styles.input}
                      placeholder="prenom@exemple.fr"
                      value={emailF}
                      onChange={(e) => setEmailF(e.target.value)}
                      aria-invalid={Boolean(errorsF.email) || undefined}
                      aria-describedby={errorsF.email ? 'famille-email-error' : undefined}
                      disabled={loadingF}
                    />
                  </div>
                  {errorsF.email && (
                    <span
                      id="famille-email-error"
                      className={styles.fieldError}
                      role="alert"
                    >
                      {errorsF.email}
                    </span>
                  )}
                </div>

                <PasswordField
                  id="famille-password"
                  name="password"
                  label="Votre mot de passe"
                  autoComplete="current-password"
                  value={passwordF}
                  onChange={(e) => setPasswordF(e.target.value)}
                  error={errorsF.password}
                  disabled={loadingF}
                />

                {errorsF.submit && (
                  <div className={styles.submitError} role="alert" aria-live="assertive">
                    {errorsF.submit}
                  </div>
                )}

                <button
                  type="submit"
                  className={styles.primaryOrange}
                  disabled={loadingF}
                  data-testid="famille-submit"
                >
                  {loadingF ? 'Connexion…' : 'Se connecter →'}
                </button>

                <p className={styles.forgotWrap}>
                  <Link to="/forgot-password/" className={styles.forgotLink}>
                    Mot de passe oublié ?
                  </Link>
                </p>

                <div className={styles.separator} aria-hidden="true">
                  <span className={styles.separatorLine} />
                  <span className={styles.separatorText}>ou</span>
                  <span className={styles.separatorLine} />
                </div>

                <Link
                  to="/signup/"
                  className={styles.outlineOrange}
                  data-testid="famille-signup"
                >
                  Créer mon compte
                </Link>

                <p className={styles.cardFooter}>
                  <span className={styles.shieldIcon} aria-hidden="true">🛡</span>
                  Découvrez Mon Temps d'Étude, puis choisissez votre
                  abonnement pour débloquer tout le parcours.
                </p>
              </form>
            </div>
          </section>

          {/* ---------- Card 2 · Enseignant ---------- */}
          <section
            className={`${styles.card} ${styles.cardEnseignant}`}
            aria-labelledby="card-enseignant-title"
          >
            <img
              src="/mte-entree-2026-09-18/moldure-carte-enseignant.png"
              alt=""
              aria-hidden="true"
              className={styles.cardFrame}
            />
            <div className={styles.cardInner}>
              <img
                src="/mte-entree-2026-09-18/icone-enseignant-bleu-nuit.png"
                alt=""
                aria-hidden="true"
                className={styles.cardIcon}
              />
              <h2 id="card-enseignant-title" className={styles.cardSubtitle}>
                Accédez à votre espace enseignant
              </h2>

              <form
                className={styles.cardForm}
                onSubmit={handleEnseignant}
                noValidate
                data-testid="enseignant-form"
              >
                <div className={styles.field}>
                  <label htmlFor="enseignant-email" className={styles.label}>
                    Votre adresse e-mail
                  </label>
                  <div
                    className={`${styles.emailWrap} ${errorsE.email ? styles.emailWrapError : ''}`}
                  >
                    <img
                      src="/mte-entree-2026-09-18/icone-email-bleu-nuit.png"
                      alt=""
                      aria-hidden="true"
                      className={styles.iconEmail}
                    />
                    <input
                      id="enseignant-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      className={styles.input}
                      placeholder="prenom@exemple.fr"
                      value={emailE}
                      onChange={(e) => setEmailE(e.target.value)}
                      aria-invalid={Boolean(errorsE.email) || undefined}
                      aria-describedby={errorsE.email ? 'enseignant-email-error' : undefined}
                      disabled={loadingE}
                    />
                  </div>
                  {errorsE.email && (
                    <span
                      id="enseignant-email-error"
                      className={styles.fieldError}
                      role="alert"
                    >
                      {errorsE.email}
                    </span>
                  )}
                </div>

                <PasswordField
                  id="enseignant-password"
                  name="password"
                  label="Votre mot de passe"
                  autoComplete="current-password"
                  value={passwordE}
                  onChange={(e) => setPasswordE(e.target.value)}
                  error={errorsE.password}
                  disabled={loadingE}
                />

                {errorsE.submit && (
                  <div className={styles.submitError} role="alert" aria-live="assertive">
                    {errorsE.submit}
                  </div>
                )}

                <button
                  type="submit"
                  className={styles.primaryBlue}
                  disabled={loadingE}
                  data-testid="enseignant-submit"
                >
                  {loadingE ? 'Connexion…' : 'Se connecter →'}
                </button>

                <p className={styles.forgotWrap}>
                  <Link to="/forgot-password/" className={styles.forgotLinkBlue}>
                    Mot de passe oublié ?
                  </Link>
                </p>

                <div className={styles.separator} aria-hidden="true">
                  <span className={styles.separatorLine} />
                  <span className={styles.separatorText}>ou</span>
                  <span className={styles.separatorLine} />
                </div>

                <Link
                  to="/signup/?role=enseignant"
                  className={styles.outlineBlue}
                  data-testid="enseignant-signup"
                >
                  Créer mon compte enseignant
                </Link>

                <p className={styles.infoBox}>
                  <span className={styles.infoIcon} aria-hidden="true">i</span>
                  Un espace dédié aux enseignants pour suivre la progression
                  de leurs élèves et accéder à des ressources pédagogiques.
                </p>

                <p className={styles.levelsChips} aria-label="Niveaux couverts">
                  <span className={styles.chip}>GS</span>
                  <span className={styles.chipDot} aria-hidden="true">·</span>
                  <span className={styles.chip}>CP</span>
                  <span className={styles.chipDot} aria-hidden="true">·</span>
                  <span className={styles.chip}>CE1</span>
                  <span className={styles.chipDot} aria-hidden="true">·</span>
                  <span className={styles.chip}>CE2</span>
                  <span className={styles.chipDot} aria-hidden="true">·</span>
                  <span className={styles.chip}>CM1</span>
                  <span className={styles.chipDot} aria-hidden="true">·</span>
                  <span className={styles.chip}>CM2</span>
                </p>
              </form>
            </div>
          </section>
        </div>

        {/* ---------- Bandeau unique ---------- */}
        <section
          className={styles.bandeauWrap}
          aria-labelledby="bandeau-title"
        >
          <img
            src="/mte-entree-2026-09-18/bandeau-apres-votre-connexion.png"
            alt=""
            aria-hidden="true"
            className={styles.bandeauBg}
          />
          <div className={styles.bandeauInner}>
            <h2 id="bandeau-title" className="visually-hidden">
              Après votre connexion
            </h2>
            <ol className={styles.bandeauSteps}>
              <li className={styles.step}>
                <span className={styles.stepText}>
                  Connectez-vous en famille pour retrouver votre espace.
                </span>
              </li>
              <li className={styles.step}>
                <span className={styles.stepText}>
                  Choisissez le profil de votre enfant dans la famille.
                </span>
              </li>
              <li className={styles.step}>
                <span className={styles.stepText}>
                  Accédez à ses missions et continuez l'aventure ensemble.
                </span>
              </li>
            </ol>
          </div>
        </section>
      </main>
    </Layout>
  );
}

export default Entrer;
